import * as React from 'react';
import { connect } from 'react-redux';
import { IAppState } from '@/app-state';
import './conversation.scss';
import { IConversationMessage } from '@/app/common/conversation/interfaces/state';
import { getFormattedLocalDateTime } from '@/app/common/util/get-formatted-date';
import { ICreateConversationPayload, ICreateConversationMessagePayload } from '@/app/common/conversation/interfaces/action';
import { EntityType } from '@/app-constants';
import { UserProfile } from '@chr/online-shared-microsite-core';
import { LoadingIndicator, Sizes, ErrorHandler } from '../components';
import * as Actions from './actions';

interface IConversationStateProps {
  conversationId: number;
  error: Error;
  isHoldRequestConversation: boolean;
  loading: boolean;
  messages: IConversationMessage[];
  purchaseOrderId: number;
  readOnly: boolean;
  entityType: EntityType;
  entityId: number;
  userProfile: UserProfile;
}

interface IConversationDispatchProps {
  getConversation: (conversationId: number, entityId: number, entityType: EntityType) => void;
  hideConversation: () => void;
  postConversation: (payload: ICreateConversationPayload) => void;
  postConversationMessage: (payload: ICreateConversationMessagePayload) => void;
}

export interface IConversationInternalState {
  message: string;
  loggedInUserDisplayName: string;
}

export type IConversationProps = IConversationStateProps & IConversationDispatchProps;

export class Conversation extends React.Component<IConversationProps, IConversationInternalState> {
  messageTextbox: React.RefObject<HTMLInputElement>;
  articleRef: React.RefObject<HTMLElement>;

  constructor(props) {
    super(props);

    this.messageTextbox = React.createRef();
    this.articleRef = React.createRef();


    this.state = {
      message: '',
      loggedInUserDisplayName: `${this.props.userProfile.firstName} ${this.props.userProfile.lastName}`
    };
  }

  componentDidMount() {
    const { current } = this.articleRef;
    if (current) {
      current.scrollTop = current.scrollHeight;
    }
  }

  componentDidUpdate() {
    const { current } = this.articleRef;
    if (current) {
      current.scrollTop = current.scrollHeight;
    }
    if (!this.props.readOnly) {
      this.messageTextbox.current.focus();
    }
  }

  messageChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      message: e.target.value
    });
  };

  postMessage = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (this.state.message.length > 0) {
      const { message } = this.state;
      const { conversationId, purchaseOrderId } = this.props;

      if (conversationId) {
        this.props.postConversationMessage({ conversationId, message, entityId: this.props.entityId, entityType: this.props.entityType });
      } else {
        // Change requests cannot exist without pre-existing conversation and is the basis for this EntityType.PurchaseOrder choice.
        this.props.postConversation({ message, entityId: purchaseOrderId, entityType: EntityType.PurchaseOrder });
      }

      this.setState({
        message: ''
      });
    }
  };

  getConversation = () => {
    if (this.props.conversationId) {
      this.props.getConversation(this.props.conversationId, this.props.entityId, this.props.entityType);
    }
  };

  renderMessages = () => {
    if (this.props.error) {
      return (
        <div className="alert">
          <p className="alert-danger">An error occurred trying to load this conversation.</p>
          <div className="alert-danger">Try reloading the conversation.</div>
        </div>
      );
    }

    if (this.props.messages.length > 0) {
      const read = this.props.messages.filter(msg => !msg.isUnread).map(this.displayMessage);
      const unread = this.props.messages.filter(msg => msg.isUnread).map(this.displayMessage);
      return (
        <React.Fragment>
          {read}
          {unread.length ? this.displayunreadSeparator() : null}
          {unread}
        </React.Fragment>
      );
    }

    return <p style={{ fontStyle: 'italic' }}>This is the start of a new conversation.</p>;
  };

  displayMessage = (message: IConversationMessage) => {
    const className = `message ${this.state.loggedInUserDisplayName === message.createdByDisplayName ? 'text-right' : 'text-left'}`;
    return (
      <div className={className} key={message.id}>
        <label className="createdByDisplayName">{message.createdByDisplayName}</label>
        <label className="createdDate">{getFormattedLocalDateTime(message.createdDate)}</label>
        <div className="content">
          {message.content}
        </div>
      </div>
    );
  };

  displayunreadSeparator = () => {
    return (
      <div className="unreadSeparator">
        <hr />
        <span>Unread Messages</span>
      </div>
    );
  };

  displayInput = () => {
    return this.props.readOnly
      ? null
      : (
        <form data-qa="conversation-form" onSubmit={this.postMessage}>
          <input
            type="text"
            value={this.state.message}
            onChange={this.messageChanged}
            ref={this.messageTextbox}
            placeholder="Enter Message. . ."
          />
          <button type="submit" className="btn btn-primary" data-qa="sendConversationBtn" disabled={this.props.loading}>Send</button>
        </form>
      );
  };

  render() {
    return (
      <div className="conversationWrapper">
        <div data-qa={`conversation_${this.props.conversationId}`} className="conversation">
          <ErrorHandler error={this.props.error} onRetryClick={this.getConversation} >
            <header>
              <div className="flex">
                <div data-qa="level-title" className="bold">
                  {this.props.entityType === EntityType.HoldRequest && (
                    'Hold Request Conversation'
                  )}
                  {this.props.entityType === EntityType.ChangeRequest && (
                    'Change Request Conversation'
                  )}
                  {this.props.entityType === EntityType.PurchaseOrder && (
                    'PO Conversation'
                  )}
                </div>
              </div>
              <button type="button" className="ns-icon ns-close" onClick={this.props.hideConversation}></button>
            </header>
            <article id="conversationArticle" ref={this.articleRef}>
              {this.props.loading ? <LoadingIndicator size={Sizes.Small} /> : this.renderMessages()}
            </article>
            {this.displayInput()}
          </ErrorHandler>
        </div>
      </div >
    );
  }
}

const mapStateToProps = (state: IAppState): IConversationStateProps => {
  const changeRequestDetail = state.poItemChangeRequestsState.changeRequests.find(cr => cr.conversationId === state.conversationState.conversationId);
  return {
    conversationId: state.conversationState.conversationId,
    error: state.conversationState.error,
    readOnly: state.conversationState.readOnly,
    isHoldRequestConversation: changeRequestDetail === undefined,
    loading: state.conversationState.loading,
    messages: state.conversationState.messages,
    purchaseOrderId: state.poDetailsState.purchaseOrderId,
    entityId: state.conversationState.entityId,
    entityType: state.conversationState.entityType,
    userProfile: state.userProfileState.userProfile,
  };
};

const mapDispatchToProps = (dispatch): IConversationDispatchProps => ({
  getConversation: (conversationId: number, entityId: number, entityType: EntityType) => {
    dispatch(Actions.conversationRequestAction({
      conversationId,
      entityId,
      entityType
    }));
  },
  hideConversation: () => {
    dispatch(Actions.conversationHideAction());
  },
  postConversation: (payload: ICreateConversationPayload) => {
    dispatch(Actions.createConversationPostAction(payload));
  },
  postConversationMessage: (payload: ICreateConversationMessagePayload) => {
    dispatch(Actions.conversationPostAction(payload));
  }
});

export const ConnectedConversation = connect<IConversationStateProps, IConversationDispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(Conversation);
